import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('email-subject-line-length');

export default function SubjectLineLengthPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'There is no optimal subject line length, only a truncation point that differs per client. Gmail on a phone shows roughly 35 characters, Gmail on desktop about 70, Outlook about 60. The practical rule follows from that: put the specific part first, because the tail may never be read.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Where each client cuts">
        <p>
          Approximate, because every one of these depends on screen width, font size, zoom and
          whether a preheader is shown alongside. Treat them as a band rather than a boundary.
        </p>
        <LearnTable
          head={['Client', 'Visible characters', 'Note']}
          rows={[
            ['Gmail, iOS/Android app', <Lead key="a">~35</Lead>, 'The tightest common case, and usually your largest audience'],
            ['Gmail, desktop web', '~70', 'Widens if the preview pane is off'],
            ['Apple Mail, iPhone portrait', '~35', 'Shows two lines of preheader beneath'],
            ['Apple Mail, iPad / desktop', '~60–70', ''],
            ['Outlook desktop', '~60', 'Narrower with the reading pane on the right'],
            ['Outlook mobile', '~40', ''],
            ['Yahoo Mail', '~46', ''],
          ]}
        />
        <p>
          The number that matters is the smallest one your audience actually uses, and for most
          senders that is the mobile Gmail app. Designing to 70 characters and assuming the rest is
          a bonus gets it backwards — for a majority of opens, 35 is the whole subject line.
        </p>
      </LearnSection>

      <LearnSection title="Front-loading, concretely">
        <p>
          The rule is not "be short". It is "be specific first". A long subject line is fine if the
          part that earns the open survives truncation.
        </p>
        <LearnCode>
          {`✗  Just following up on our conversation about your Q3 reporting requirements
   mobile Gmail shows:  "Just following up on our conve…"
   → indistinguishable from every other follow-up in the inbox

✓  Q3 reporting: the two numbers we discussed
   mobile Gmail shows:  "Q3 reporting: the two numbers…"
   → the specific thing is already visible`}
        </LearnCode>
        <p>
          Both are about the same length. The second works on a phone because the first four words
          carry the information, which is the entire technique.
        </p>
      </LearnSection>

      <LearnSection title="The preheader is the other half">
        <p>
          Every client shows preview text beside or beneath the subject — typically 35 to 90
          characters, and on iPhone up to two full lines. It is drawn from the first text in the
          message body, which means if you do not set it deliberately, it is set for you.
        </p>
        <p>The default is usually one of the worst possible strings:</p>
        <LearnList>
          <li><em>"View this email in your browser"</em> — the most common preheader on the internet</li>
          <li><em>"Hi {'{{'}first_name{'}}'},"</em> — a merge tag and nothing else</li>
          <li>An unsubscribe line, if the template puts it near the top</li>
          <li>Nothing at all, when the first element is an image</li>
        </LearnList>
        <p>
          Setting it explicitly effectively doubles your visible space. It is the highest-value,
          lowest-effort change on this page, and most senders never touch it because it is not a
          field they see in the editor.
        </p>
      </LearnSection>

      <LearnSection title="What length does not do">
        <LearnSubsection title="It does not affect deliverability">
          <p>
            A long subject line does not send you to spam. Placement is decided by authentication,
            reputation and engagement — length affects whether someone opens a message that already
            reached the inbox. Those are different problems with different causes, and{' '}
            <Link to="/learn/why-email-goes-to-spam" className="text-accent underline underline-offset-4">
              the placement one
            </Link>{' '}
            is much larger.
          </p>
        </LearnSubsection>
        <LearnSubsection title="There is no benchmark that transfers">
          <p>
            "41 characters is optimal" comes from someone else's list, audience and offer. Their
            median is not a target for you, and treating it as one optimises toward a stranger's
            data.
          </p>
        </LearnSubsection>
        <LearnSubsection title="And the metric you would test it with is broken">
          <p>
            Length is the classic A/B test, and open rate is the classic way to measure it — which
            has not been trustworthy since{' '}
            <Link to="/learn/email-open-rate" className="text-accent underline underline-offset-4">
              Apple Mail Privacy Protection
            </Link>{' '}
            began pre-loading tracking pixels. Test on clicks or replies, or you are measuring
            Apple's proxy servers rather than your readers.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="A workable checklist">
        <LearnList ordered>
          <li><Lead>Put the specific noun in the first 35 characters.</Lead> Not the greeting, not the framing.</li>
          <li><Lead>Set the preheader deliberately.</Lead> Continue the subject rather than repeating it.</li>
          <li><Lead>Cut the opener.</Lead> "Quick question about", "Following up on" and "I wanted to reach out regarding" spend your visible budget on nothing.</li>
          <li><Lead>Skip emoji unless your audience expects them.</Lead> They render inconsistently, count against the character budget, and read as promotional in B2B.</li>
          <li><Lead>Preview on a real phone.</Lead> Every character estimate on this page is approximate; your own inbox is not.</li>
        </LearnList>
        <p>
          <Link to="/tools/subject-line-tester" className="text-accent underline underline-offset-4">
            The subject-line tester
          </Link>{' '}
          scores length alongside caps ratio, punctuation and{' '}
          <Link to="/learn/spam-trigger-words" className="text-accent underline underline-offset-4">
            vocabulary
          </Link>
          , which is a faster first pass than opening five mail clients.
        </p>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is the ideal email subject line length?',
    a: 'There is no ideal length, only a truncation point that differs per client. Gmail on a phone shows roughly 35 characters, Gmail on desktop about 70, Outlook about 60. What matters is that the specific part appears before the cut, not that the whole line is short.',
  },
  {
    q: 'How many characters does Gmail show?',
    a: 'Around 35 in the mobile app and around 70 on desktop web, both varying with screen width, font size and whether a preview pane is open. The mobile figure is the one to design to, since it is usually the larger audience.',
  },
  {
    q: 'Does subject line length affect deliverability?',
    a: 'No. Placement is decided by authentication, sender reputation and recipient engagement. Length affects whether someone opens a message that already reached the inbox — a different problem, and a much smaller one.',
  },
  {
    q: 'What is preheader text and why does it matter?',
    a: 'The preview text shown beside or beneath the subject, typically 35 to 90 characters and up to two lines on iPhone. It is pulled from the first text in your body, so if you do not set it, your reader sees "View this email in your browser". Setting it roughly doubles your visible space.',
  },
  {
    q: 'Should I use emoji in subject lines?',
    a: 'Only if your audience expects them. They render inconsistently across clients, count against a tight character budget, and read as promotional in B2B contexts where the sender is trying to look like a person rather than a campaign.',
  },
  {
    q: 'How should I test subject line length?',
    a: 'On clicks or replies, not opens. Open rate has not been a reliable metric since Apple Mail Privacy Protection began pre-loading tracking pixels, so a length test measured on opens is largely measuring Apple’s proxy servers.',
  },
];
